import React from 'react'

import { ActionIcon, Menu, Tooltip } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useRoute } from 'wouter'

import { FancyMenuItem } from '#components/FancyMenuItem'
import {
  showHeadersFootersModal,
  showPushScriptToPrompterModal,
  showUpgradeInquiryModal,
} from '#components/Modals'
import { showMoresAndContinuedsModal } from '#components/Modals/MoresAndContinueds'
import { showPrintScriptModal } from '#components/Modals/PrintScriptModal'
import { showPrintSectionModal } from '#components/Modals/PrintSectionModal'
import { showPrompterViewInfo } from '#components/PrompterView'
import { STRIPE_TIER } from '#components/Settings/Org/plans'
import { Spinner } from '#components/Spinner'
import { ExportOptions, Keys, useFileCreation, useShortcuts } from '#hooks'
import { useMst } from '#state'
import { ROUTE_PATTERNS } from '#util/pathConfigs'

import { PaidMenuItem } from './PaidMenuItem'
import { PaidMenuNote } from './PaidMenuNote'

import styles from './ExtrasMenu.module.scss'

export const ExtrasMenu = observer(function ExtrasMenu() {
  const { currentOrg, user, currentScript, view } = useMst()
  const isUnpaid = !!currentOrg?.isUnpaid
  const creation = useFileCreation()
  const maxHeight = view.dimensions.scriptScroller.height - 20
  const [isOpen, setIsOpen] = React.useState(false)
  const [isSplitEditor] = useRoute(ROUTE_PATTERNS.splitEditor)

  const handleOpenChange = (value: boolean) => {
    if (isOpen && !value) {
      currentScript?.observableEditor?.focusEditor()
    }
    setIsOpen(value)
  }

  const showPrompterSubtitle =
    currentOrg?.hasPrompterAvailable &&
    currentOrg?.hasPrompterDisabled &&
    !currentScript?.isPrivate

  const upsellSubtitle =
    currentOrg?.tier === STRIPE_TIER
      ? 'Upgrade to a plan to enable'
      : 'Contact us to enable'

  const p2pDisabled = currentScript?.isPrivate || !user.canPushToPrompter

  const handleCreatePdf = (fileType: 'print' | 'download') => {
    creation.createFile({ fileType, script: currentScript })
  }

  const handleExport = (fileType: ExportOptions) => {
    creation.createFile({ fileType, script: currentScript })
  }

  const handlePrompterPush = () => {
    if (currentScript) {
      showPushScriptToPrompterModal(currentScript)
    }
  }

  const { getItemProps } = useShortcuts(
    {
      print: {
        keys: [Keys.CMD, 'P'],
        action: () => {
          handleCreatePdf('print')
        },
      },
      export: {
        keys: [Keys.CMD, Keys.SHIFT, 'E'],
        action: () => handleCreatePdf('download'),
      },
      p2p: {
        keys: [Keys.CMD, Keys.SHIFT, 'P'],
        action: handlePrompterPush,
        disabled: p2pDisabled,
      },
    },
    {
      skip: isSplitEditor,
    },
  )

  if (!currentOrg || !currentScript) return null

  const handlePrompterView = () => {
    currentScript.setShowPrompterView(true)
    if (user.prefs.prompterView === undefined) {
      showPrompterViewInfo()
    }
  }

  const { isScreenplay } = currentScript

  const characterReportTitle = `Export ${
    isScreenplay ? 'character' : 'cast'
  } report`

  return (
    <>
      {creation.isCreating && <Spinner delayMs={300} fullScreen />}
      <Menu position="bottom-end" opened={isOpen} onChange={handleOpenChange}>
        <Menu.Target>
          <Tooltip label="Export and more" disabled={isOpen}>
            <ActionIcon color="dark" variant="subtle">
              <i className="fa fa-ellipsis-h"></i>
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown className={styles.extras_dropdown} style={{ maxHeight }}>
          {/* only render innards when open to avoid unncessary re-renders */}
          {isOpen && (
            <>
              <FancyMenuItem title="Print..." {...getItemProps('print')} />
              {currentScript.isInlinePaginated && (
                <FancyMenuItem
                  title="Print section..."
                  onClick={() =>
                    showPrintSectionModal({ script: currentScript })
                  }
                />
              )}
              <FancyMenuItem
                title="Export PDF"
                {...getItemProps('export')}
              ></FancyMenuItem>
              <FancyMenuItem
                title="Print preferences..."
                onClick={() =>
                  showPrintScriptModal({
                    docType: 'script',
                    script: currentScript,
                  })
                }
              ></FancyMenuItem>
              <FancyMenuItem
                title="Headers and footers..."
                onClick={() =>
                  showHeadersFootersModal({
                    docType: 'script',
                    script: currentScript,
                  })
                }
              />

              <Menu.Divider />
              <FancyMenuItem
                title="Export to Final Draft"
                onClick={() => handleExport('fdx')}
              ></FancyMenuItem>
              <FancyMenuItem
                title="Export Fountain"
                onClick={() => handleExport('fountain')}
              ></FancyMenuItem>
              <FancyMenuItem
                title="Export line data"
                onClick={() => handleExport('line-data')}
              ></FancyMenuItem>
              <Menu.Divider />
              <PaidMenuItem
                title={characterReportTitle}
                onClick={() => handleExport('characterReport')}
                isUnpaidOrg={isUnpaid}
              ></PaidMenuItem>
              {!isScreenplay && (
                <PaidMenuItem
                  title="Export brackets list"
                  onClick={() => handleExport('bracketsList')}
                  isUnpaidOrg={isUnpaid}
                ></PaidMenuItem>
              )}
              {isScreenplay && (
                <>
                  <Menu.Divider />
                  <FancyMenuItem
                    title="Mores and continueds..."
                    onClick={() => showMoresAndContinuedsModal(currentScript)}
                  ></FancyMenuItem>
                </>
              )}
              {!isScreenplay && (
                <>
                  <Menu.Divider />
                  <FancyMenuItem
                    title="Prompter view"
                    onClick={handlePrompterView}
                  ></FancyMenuItem>
                  <PaidMenuItem
                    title="Export prompter text"
                    onClick={() => handleExport('prompter')}
                    isUnpaidOrg={isUnpaid}
                  ></PaidMenuItem>
                  <FancyMenuItem
                    title="Push to prompter..."
                    subTitle={showPrompterSubtitle ? upsellSubtitle : undefined}
                    {...getItemProps('p2p')}
                  ></FancyMenuItem>
                </>
              )}
              {currentOrg.tier !== STRIPE_TIER && isUnpaid && (
                <>
                  <Menu.Divider />
                  <Menu.Item
                    onClick={() => showUpgradeInquiryModal(currentOrg)}
                  >
                    Request an upgrade ✨
                  </Menu.Item>
                </>
              )}
              <PaidMenuNote
                features={
                  isScreenplay
                    ? [{ name: 'character reports', plural: true }]
                    : [
                        { name: 'cast reports' },
                        { name: 'brackets lists' },
                        { name: 'prompter text' },
                      ]
                }
              />
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  )
})
